import { PRIORITY_LABELS, type ActivityDto, type Priority } from '@kanban/shared';
import { formatDate, formatDateTime } from './format.js';

/**
 * Verlaufseintraege in deutsche Saetze uebersetzen (SPEC 9.6).
 *
 * Der Name des Handelnden steht daneben, nicht hier — die Funktion liefert
 * genau den Teil dahinter: „hat die Aufgabe von Offen nach In Arbeit
 * verschoben".
 *
 * Alle Werte kommen aus einem jsonb-Feld und werden deshalb geprueft statt
 * angenommen. Ein Eintrag von vor zwei Codegenerationen darf die Anzeige
 * nicht zum Absturz bringen; er faellt hoechstens weg.
 */

function text(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function quoted(value: string | null): string {
  return value ? `„${value}"` : 'sie';
}

function priorityName(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  if (typeof value !== 'number') return null;
  return PRIORITY_LABELS[value as Priority] ?? null;
}

function dueText(payload: Record<string, unknown>): string {
  const dueAt = text(payload, 'dueAt');
  if (!dueAt) return 'hat die Fälligkeit entfernt';

  const date = new Date(dueAt);
  if (Number.isNaN(date.getTime())) return 'hat die Fälligkeit geändert';

  const withTime = payload.dueHasTime === true;
  return `hat die Fälligkeit auf ${withTime ? formatDateTime(date) : formatDate(date)} gesetzt`;
}

/** `null` = dieser Eintrag laesst sich nicht formulieren und wird ausgelassen. */
export function describeActivity(entry: Pick<ActivityDto, 'type' | 'payload'>): string | null {
  const p = entry.payload;

  switch (entry.type) {
    case 'card.created':
      if (p.fromRecurrence === true) return 'hat die Aufgabe als Wiederholung angelegt';
      return text(p, 'columnTitle')
        ? `hat die Aufgabe in ${quoted(text(p, 'columnTitle'))} angelegt`
        : 'hat die Aufgabe angelegt';

    case 'card.renamed': {
      const to = text(p, 'to');
      const from = text(p, 'from');
      if (!to) return 'hat den Titel geändert';
      return from
        ? `hat den Titel von ${quoted(from)} auf ${quoted(to)} geändert`
        : `hat den Titel auf ${quoted(to)} geändert`;
    }

    case 'card.described':
      return p.cleared === true ? 'hat die Beschreibung entfernt' : 'hat die Beschreibung geändert';

    case 'card.moved': {
      const from = text(p, 'from');
      const to = text(p, 'to');
      if (!to) return 'hat die Aufgabe verschoben';
      return from
        ? `hat die Aufgabe von ${quoted(from)} nach ${quoted(to)} verschoben`
        : `hat die Aufgabe nach ${quoted(to)} verschoben`;
    }

    case 'card.movedToBoard': {
      const from = text(p, 'fromBoard');
      return from
        ? `hat die Aufgabe von Board ${quoted(from)} hierher verschoben`
        : 'hat die Aufgabe von einem anderen Board hierher verschoben';
    }

    case 'card.due':
      return dueText(p);

    case 'card.priority': {
      const to = priorityName(p, 'to');
      if (to === null) return 'hat die Priorität geändert';
      return to === PRIORITY_LABELS[0]
        ? 'hat die Priorität entfernt'
        : `hat die Priorität auf ${to} gesetzt`;
    }

    case 'card.completed':
      return 'hat die Aufgabe erledigt';
    case 'card.reopened':
      return 'hat die Aufgabe wieder geöffnet';
    case 'card.archived':
      return 'hat die Aufgabe archiviert';
    case 'card.restored':
      return 'hat die Aufgabe zurückgeholt';

    case 'card.assigned': {
      const name = text(p, 'userName');
      return name ? `hat ${name} eingetragen` : 'hat jemanden eingetragen';
    }
    case 'card.unassigned': {
      const name = text(p, 'userName');
      return name ? `hat ${name} ausgetragen` : 'hat jemanden ausgetragen';
    }

    case 'card.labeled':
      return `hat das Label ${quoted(text(p, 'labelName'))} gesetzt`;
    case 'card.unlabeled':
      return `hat das Label ${quoted(text(p, 'labelName'))} entfernt`;

    case 'comment.created': {
      const excerpt = text(p, 'excerpt');
      return excerpt ? `hat kommentiert: „${excerpt}"` : 'hat kommentiert';
    }
    case 'comment.deleted':
      return 'hat einen Kommentar gelöscht';

    case 'attachment.added': {
      const filename = text(p, 'filename');
      return filename ? `hat ${quoted(filename)} angehängt` : 'hat eine Datei angehängt';
    }
    case 'attachment.deleted': {
      const filename = text(p, 'filename');
      return filename ? `hat ${quoted(filename)} entfernt` : 'hat einen Anhang entfernt';
    }

    case 'column.created':
      return `hat die Spalte ${quoted(text(p, 'title'))} angelegt`;
    case 'column.renamed': {
      const from = text(p, 'from');
      const to = text(p, 'to');
      if (!to) return 'hat eine Spalte umbenannt';
      return from
        ? `hat die Spalte ${quoted(from)} in ${quoted(to)} umbenannt`
        : `hat eine Spalte in ${quoted(to)} umbenannt`;
    }
    case 'column.archived':
      return `hat die Spalte ${quoted(text(p, 'title'))} archiviert`;
    case 'column.restored':
      return `hat die Spalte ${quoted(text(p, 'title'))} zurückgeholt`;

    default:
      return null;
  }
}
